/** @jsxImportSource @emotion/react */
import type {
  OptumPayerIdCheck,
  OptumPayerTransactionCheck,
} from 'ts-back-end/src/business/insurance-verification/types/types'

import { confirm } from 'components/molecules/Confirmation'
import { useLazyCheckLabPayerOptumIdsQuery } from 'store/services/payer'
import {
  OPTUM_PAYER_CHECK_MESSAGES,
  OPTUM_PAYER_ID_FIELDS,
  OPTUM_PAYER_ID_SOURCE_LABELS,
  OPTUM_RESOLVED_PAYER_ID_LABELS,
} from 'components/lab/constants/payors'
import { ClaimSubmissionMethod, LabPayerFormValues } from 'components/lab/orderEdit/payor/LabPayerForm'

type OptumPayerIds = Record<(typeof OPTUM_PAYER_ID_FIELDS)[number], string>
type OptumResolvedIdField = keyof typeof OPTUM_RESOLVED_PAYER_ID_LABELS

const UNAVAILABLE: OptumPayerIdCheck = { available: false, eligibility: null, claims: null }

const pickPayerIds = (values: Partial<LabPayerFormValues>): OptumPayerIds =>
  Object.fromEntries(
    OPTUM_PAYER_ID_FIELDS.map((field) => [field, values[field]?.trim() ?? '']),
  ) as OptumPayerIds

// Mirrors the back end: a changed payor ID drops saved Optum IDs that weren't changed with it.
const getClearedIdFields = (ids: OptumPayerIds, initialIds: OptumPayerIds): OptumResolvedIdField[] => {
  if (!initialIds.uPayerId || ids.uPayerId === initialIds.uPayerId) {
    return []
  }

  return (Object.keys(OPTUM_RESOLVED_PAYER_ID_LABELS) as OptumResolvedIdField[]).filter(
    (field) => initialIds[field] && ids[field] === initialIds[field],
  )
}

const getWarning = (check: OptumPayerTransactionCheck | null, unrecognized: string) => {
  if (!check) {
    return null
  }

  if (!check.matches.length) {
    return unrecognized
  }

  return check.matches.length > 1 ? OPTUM_PAYER_CHECK_MESSAGES.multipleMatches : null
}

const CheckSection = ({
  title,
  check,
  warning,
}: {
  title: string
  check: OptumPayerTransactionCheck | null
  warning: string | null
}) =>
  check && (
    <div className="mb-3">
      <strong>{title}</strong>{' '}
      <span className="text-muted">
        ({check.checkedId}, from {OPTUM_PAYER_ID_SOURCE_LABELS[check.source]})
      </span>
      {warning && <div className="text-danger">{warning}</div>}
      {check.matches.length > 0 && (
        <ul className="mb-0">
          {check.matches.map((match) => (
            <li key={`${match.payerId}|${match.payerPlanName}`}>
              {match.payerPlanName} — ID {match.payerId}
              {match.claimsCpid && match.claimsCpid !== match.payerId && `, CPID ${match.claimsCpid}`}
              {match.status && ` (${match.status})`}
            </li>
          ))}
        </ul>
      )}
    </div>
  )

// Before saving changed payer IDs, shows what Optum matched and warns on unknown IDs. Never blocks.
export const useOptumPayerIdConfirmation = () => {
  const [checkOptumPayerIds] = useLazyCheckLabPayerOptumIdsQuery()

  return async (
    values: LabPayerFormValues,
    initialValues: Partial<LabPayerFormValues>,
  ): Promise<boolean> => {
    const ids = pickPayerIds(values)
    const initialIds = pickPayerIds(initialValues)

    if (OPTUM_PAYER_ID_FIELDS.every((field) => ids[field] === initialIds[field])) {
      return true
    }

    const clearedFields = getClearedIdFields(ids, initialIds)
    const savedIds = { ...ids, ...Object.fromEntries(clearedFields.map((field) => [field, ''])) }

    const result = await checkOptumPayerIds(savedIds)
      .unwrap()
      .catch(() => UNAVAILABLE)

    const { eligibility } = result
    const claims = values.claimSubmissionMethod === ClaimSubmissionMethod.PAPER ? null : result.claims

    if (result.available && !eligibility && !claims && !clearedFields.length) {
      return true
    }

    const eligibilityWarning = getWarning(eligibility, OPTUM_PAYER_CHECK_MESSAGES.eligibilityUnrecognized)
    const claimsWarning = getWarning(claims, OPTUM_PAYER_CHECK_MESSAGES.claimsUnrecognized)
    const hasWarning = !result.available || !!eligibilityWarning || !!claimsWarning

    try {
      await confirm('', {
        title: hasWarning ? 'Check the payer ID' : 'Confirm the Optum payer',
        size: 'lg',
        description: () => (
          <>
            {clearedFields.length > 0 && (
              <div className="mb-3">
                {OPTUM_PAYER_CHECK_MESSAGES.resolvedIdsCleared}
                <ul className="mb-0">
                  {clearedFields.map((field) => (
                    <li key={field}>
                      {OPTUM_RESOLVED_PAYER_ID_LABELS[field]}: {initialIds[field]}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {result.available ? (
              <>
                <CheckSection title="Eligibility" check={eligibility} warning={eligibilityWarning} />
                <CheckSection title="Claims" check={claims} warning={claimsWarning} />
              </>
            ) : (
              <div className="text-danger">{OPTUM_PAYER_CHECK_MESSAGES.unavailable}</div>
            )}
          </>
        ),
        yesAction: hasWarning ? 'Save anyway' : 'Save',
        noAction: 'Back',
      })

      return true
    } catch {
      return false
    }
  }
}
